import { defineStore } from 'pinia';
import { reactive, readonly, watch } from 'vue';
import { parseDataFromBrowserStorage } from '@/utils/storage';
import { BrowserStorageKey } from '@/constants/browserStorage';
import { useUserStore } from '@/stores/useUserStore';
import { PORTA_DOMAIN } from '@/constants/domain';
import { useUserSettings, useUpdateUserSettings } from '@/api/useUserSettings';
import { useI18n } from 'vue-i18n';

export type Language = 'de' | 'en' | 'fr' | 'cs' | 'pl';

export interface ClientSideSettings {
  focusOnShortcut: boolean;
  showPickupBanner: boolean;
  playPickupSound: boolean;
  callOnShortcut: boolean;
  phoneEnabled: boolean;
  lastClientVersion: string | null;
  hotkeyEnabled: boolean;
  locale: string;
}

const defaultClientSideSettings: ClientSideSettings = {
  focusOnShortcut: true,
  playPickupSound: false,
  showPickupBanner: false,
  lastClientVersion: null,
  hotkeyEnabled: true,
  callOnShortcut: false,
  phoneEnabled: true,
  locale: 'en',
};

export const useClientSideSettingsStore = defineStore('settings', () => {
  const $userStore = useUserStore();
  const i18n = useI18n();
  const { data: backendSettings, isLoading } = useUserSettings();
  const { mutate: saveToBackend } = useUpdateUserSettings();

  const settings = reactive<ClientSideSettings>({ ...defaultClientSideSettings });
  const _settings = readonly(settings);

  let hasLoadedFromBackend = false;

  const isPortaDomain = () => $userStore._loggedInUser.domain === PORTA_DOMAIN;
  const getLastClientVersionFromStorage = (): string | null => localStorage.getItem(BrowserStorageKey.LAST_CLIENT_VERSION);

  const getDefaultSettings = (): ClientSideSettings => ({
    ...defaultClientSideSettings,
    lastClientVersion: getLastClientVersionFromStorage(),
    callOnShortcut: isPortaDomain(),
    phoneEnabled: !isPortaDomain(),
    locale: (i18n?.locale?.value as Language) || 'en',
  });

  const getLegacyStorageSettings = (): Partial<ClientSideSettings> | null => {
    return parseDataFromBrowserStorage<ClientSideSettings>(BrowserStorageKey.SETTINGS_DATA);
  };

  const migrateLegacySettings = () => {
    const legacySettings = getLegacyStorageSettings();
    if (legacySettings?.lastClientVersion) localStorage.setItem(BrowserStorageKey.LAST_CLIENT_VERSION, legacySettings.lastClientVersion);
    localStorage.removeItem(BrowserStorageKey.SETTINGS_DATA);
  };

  const initializeSettings = () => {
    const defaults = getDefaultSettings();
    const legacySettings = getLegacyStorageSettings();
    Object.assign(settings, defaults, legacySettings ?? {});
  };

  const saveSettingsToBackend = () => saveToBackend({ ...settings });

  const loadSettingsFromBackend = (backendData: ClientSideSettings) => {
    const localVersion = getLastClientVersionFromStorage();
    const { lastClientVersion: _, ...settingsWithoutVersion } = backendData;

    Object.assign(settings, settingsWithoutVersion, { lastClientVersion: localVersion });
    localStorage.setItem(BrowserStorageKey.LANGUAGE, backendData.locale);
  };

  watch(
    () => backendSettings.value,
    (response) => {
      if (response?.settings) {
        loadSettingsFromBackend(response.settings);
        migrateLegacySettings();
        hasLoadedFromBackend = true;
      } else if (!isLoading.value && !hasLoadedFromBackend) {
        hasLoadedFromBackend = true;
        migrateLegacySettings();
        saveToBackend({ ...settings });
      }
    }
  );

  watch(
    () => i18n?.locale?.value,
    (newLocale) => {
      if (newLocale && hasLoadedFromBackend && settings.locale !== newLocale) {
        settings.locale = newLocale as Language;
        localStorage.setItem(BrowserStorageKey.LANGUAGE, newLocale);
        saveSettingsToBackend();
      }
    }
  );

  const toggleShowPickupBanner = () => {
    settings.showPickupBanner = !settings.showPickupBanner;
    saveSettingsToBackend();
  };

  const togglePlayPickupSound = () => {
    settings.playPickupSound = !settings.playPickupSound;
    saveSettingsToBackend();
  };

  const toggleCallOnShortcut = () => {
    settings.callOnShortcut = !settings.callOnShortcut;
    saveSettingsToBackend();
  };

  const toggleFocusOnShortcut = () => {
    settings.focusOnShortcut = !settings.focusOnShortcut;
    saveSettingsToBackend();
  };

  const togglePhone = () => {
    settings.phoneEnabled = !settings.phoneEnabled;
    saveSettingsToBackend();
  };

  const toggleHotkey = () => {
    settings.hotkeyEnabled = !settings.hotkeyEnabled;
    applyHotkeySetting();
    saveSettingsToBackend();
  };

  const setLastClientVersion = (version: string) => {
    settings.lastClientVersion = version;
    localStorage.setItem(BrowserStorageKey.LAST_CLIENT_VERSION, version);
    saveSettingsToBackend();
  };

  const applyHotkeySetting = () => {
    if (!settings.hotkeyEnabled) {
      window.wtgPhoneApi.setCallShortcut({ shortcut: null });
      return;
    }

    const shortcut = isPortaDomain() ? 'CommandOrControl+Shift+C' : 'F6';
    const releaseKeys = isPortaDomain() ? ['command', 'control', 'shift', 'c'] : ['f6'];

    window.wtgPhoneApi.setCallShortcut({ shortcut, releaseKeys });
  };

  return {
    _settings,
    initializeSettings,
    toggleShowPickupBanner,
    togglePlayPickupSound,
    toggleCallOnShortcut,
    toggleFocusOnShortcut,
    togglePhone,
    setLastClientVersion,
    toggleHotkey,
    applyHotkeySetting,
  };
});
